import { useTranslation } from "@i18n";
import { convertDate } from "../../services/luxonHelper";

/** One custom field entry; `customMetaData`/`customFields` are `z.array(z.unknown())` on the server. */
type IssueCustomField = { title?: string; type?: string; value?: string | number };

/**
 * Narrows one `z.unknown()` entry; every property is optional, so anything of an unexpected type is
 * dropped to `undefined` rather than discarding the whole entry.
 */
function toIssueCustomField(entry: unknown): IssueCustomField {
  if (typeof entry !== "object" || entry === null) {
    return {};
  }
  const title = "title" in entry && typeof entry.title === "string" ? entry.title : undefined;
  const type = "type" in entry && typeof entry.type === "string" ? entry.type : undefined;
  const rawValue = "value" in entry ? entry.value : undefined;
  const value = typeof rawValue === "string" || typeof rawValue === "number" ? rawValue : undefined;
  return { title, type, value };
}

interface CustomFieldsProps {
  /** IssueDetails passes `issue.customMetaData`, viewIssue passes `issuesData.customFields`. */
  customMetaData?: unknown[];
  dueDate?: string | number | null;
}

function CustomFields({ customMetaData, dueDate }: Readonly<CustomFieldsProps>) {
  const { t } = useTranslation();
  const customFields = customMetaData?.map((customField: unknown) => {
    const data = toIssueCustomField(customField);
    if (data.type === "DATE") {
      data.value = convertDate(Number(data.value), "dateForText");
    }
    return data;
  });

  return (
    <div className="my-2 flex w-full flex-col items-start gap-4 text-xs text-[var(--text-light)]">
      {dueDate && (
        <div className="flex items-center justify-start gap-3 text-xs">
          <span className="truncate text-[var(--text-light)]">{t("Due Date")}</span>
          <span className="flex items-center gap-2.5 rounded bg-[var(--surface-gradient-color2)] px-2 py-0.5 text-xs font-semibold text-[var(--primary-text)]">
            <span>{convertDate(Number(dueDate), "dateForText")}</span>
          </span>
        </div>
      )}
      {customFields?.map((customField: IssueCustomField) => (
        <div key={customField.title} className="flex items-center justify-start gap-3 text-xs">
          <span className="truncate text-[var(--text-light)]">{customField.title}</span>
          <span className="flex items-center gap-2.5 rounded bg-[var(--surface-gradient-color2)] px-2 py-0.5 text-xs font-semibold text-[var(--primary-text)]">
            <span>{customField.value}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

export default CustomFields;
